import { useState } from 'react'
import moment from 'moment'
import { toast } from 'react-toastify'

import TabHeader from 'components/atoms/TabHeader'
import Button from 'components/atoms/Button'
import { Order } from 'store/types'
import { OxygenDeliveryModal } from './OxygenDeliveryModal'
import { fullName } from 'helpers/nameHelper'
import TitleLead from '../../../atoms/TitleLead'
import { useUpdateOxygenDeliveryMutation, OxygenDeliveryDto } from 'store/services/dmeOrder'

interface IProps {
  order: Order
  refetchOrder: () => void
}

const OxygenSetupTab = ({ order, refetchOrder }: IProps) => {
  const [oxygenModalOpen, setOxygenModalOpen] = useState(false)
  const [updateOxygenDelivery] = useUpdateOxygenDeliveryMutation()

  const uploadProgress = order?.progress?.UPLOAD
  const ahiValues = order?.reportRenderRequests?.map(report => report?.type === 'REPORT_PDF' && report?.ahi).filter(ahi => typeof ahi === 'number')
  const oxygenDeliveryDate = order?.oxygenDeliveryDate
  const oxygenNonDeliveryReason = order?.oxygenNonDeliveryReason
  
  const deliveryType = order?.testType === 'HST' ? 'PAP' : 'O2'

  const getQualifierLabel = () => {
    if (order?.testType === 'POX') {
      return uploadProgress === 'G1' ? 'Group 1' : uploadProgress === 'G2' ? 'Group 2' : ''
    }
    if (order?.testType === 'HST' && ahiValues?.some(value => value > 5)) {
      return `AHI: ${ahiValues?.join('/')}`
    }
    return ''
  }

  const handleO2ModalSave = async (data: OxygenDeliveryDto) => {
    try {
      await updateOxygenDelivery({
        orderId: Number(order.id),
        ...data,
      }).unwrap()

      setOxygenModalOpen(false)
      toast.success('Oxygen delivery status saved successfully!')
      refetchOrder()
    } catch (error) {
      toast.error('Failed to save Oxygen delivery status')
      console.error('Error saving Oxygen delivery status:', error)
    }
  }

  const hasData = !!oxygenDeliveryDate || !!oxygenNonDeliveryReason

  return (
    <div className="mx-2 p-3">
      <TabHeader title={`${deliveryType} Setup`} />

      <div className="row px-2 mb-2">
        <div className="col px-4 d-flex flex-column border bg-white pb-3">
          <div className="row align-items-center">
            <div className="col my-2 d-flex justify-content-between">
              <div className="d-flex align-items-center">
                <TitleLead className="my-2" label={`${deliveryType} Delivery Setup`} />
                {getQualifierLabel() && (
                  <span className="badge badge-info ml-2">
                    {getQualifierLabel()}
                  </span>
                )}
              </div>
              <Button
                onClick={() => setOxygenModalOpen(true)}
                className="btn-primary btn btn-sm"
                icon="fas fa-pencil-alt"
                label={`Edit ${deliveryType} Delivery Status`}
              />
            </div>
            <hr className="w-100 mt-0" />
          </div>

          {hasData ? (
            <div className="row">
              <div className="col">
                {oxygenDeliveryDate ? (
                  <>
                    <span className="font-weight-bold textGray">Delivery Date: </span>
                    <span>{moment(oxygenDeliveryDate).format('MM/DD/YYYY')}</span>
                  </>
                ) : oxygenNonDeliveryReason ? (
                  <>
                    <span className="font-weight-bold textGray">Non-Delivery Reason: </span>
                    <span>{oxygenNonDeliveryReason}</span>
                  </>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="card mt-0 ml-2 mr-2 mb-3">
              <div className="card-body text-center">
                <i className="fas fa-info-circle text-info mr-2" />
                <span className="font-italic text-muted">
                  {deliveryType} delivery status is not documented yet. Click "Edit {deliveryType} Delivery Status" to add.
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <OxygenDeliveryModal
        show={oxygenModalOpen}
        onHide={() => setOxygenModalOpen(false)}
        onSave={handleO2ModalSave}
        orderId={order.id?.toString() || ''}
        patientName={fullName(order?.patient?.firstName, order?.patient?.lastName)}
        oxygenDeliveryDate={oxygenDeliveryDate}
        oxygenNonDeliveryReason={oxygenNonDeliveryReason}
        deliveryType={order?.testType === 'POX' ? 'O2' : 'PAP'}
      />
    </div>
  )
}

export default OxygenSetupTab
